import React, { useMemo } from "react";
import classNames from "classnames";

import { Select as MuiSelect, SelectProps } from "@mui/material";
import { ReactComponent as CaretDownSVG } from "App/common/icons-v2/carat-down.svg";
import { useTheme } from "./ThemeProvider";
import "./Menu/styles.scss";
import {
  ButtonV2Color,
  ButtonV2Shape,
  ButtonV2Size,
  ButtonV2Variant,
} from "App/core/ButtonV2";
import styles from "./Select.module.scss";

export type AppSelectProps = Omit<
  SelectProps,
  "size" | "variant" | "color" | "theme"
> & {
  size?: ButtonV2Size;
  variant?: ButtonV2Variant;
  color?: ButtonV2Color;
  shape?: ButtonV2Shape;
  noPadding?: boolean;
  // This is to remove the padding used to create space for the endAdornment/IconComponent
  removeIconPadding?: boolean;
};

export const Select = ({
  endAdornment,
  className,
  disabled,
  size = "sm",
  variant = "solid",
  color = "secondary",
  shape = "rounded",
  noPadding,
  removeIconPadding,
  ...props
}: AppSelectProps) => {
  const { theme: darkOrLightTheme } = useTheme();

  const MenuProps = useMemo(
    () => ({
      ...(props.MenuProps || {}),
      className: classNames("highnote-menu", props.MenuProps?.className),
      "data-theme": darkOrLightTheme,
    }),
    [props.MenuProps, darkOrLightTheme],
  );

  return (
    <MuiSelect
      {...props}
      className={classNames(
        styles["highnote-select"],
        styles[color],
        styles[variant],
        styles[shape],
        // These size styles are to handle border radius and shape, in relation to the size
        styles[size],
        {
          [styles["no-padding"]]: noPadding,
        },
        className,
      )}
      SelectDisplayProps={{
        className: classNames(
          styles["highnote-select-display"],
          styles[shape],
          // Main size styles are attached here
          styles[size],
          removeIconPadding && styles["remove-icon-padding"],
          props.SelectDisplayProps?.className,
        ),
      }}
      MenuProps={MenuProps}
      IconComponent={() => null} // Hide the default caret icon
      endAdornment={endAdornment || <CaretDownSVG />}
      data-theme={darkOrLightTheme}
      data-disabled={!!disabled}
      sx={{
        // Since we have custom styles, we need to override the default styles below
        // These default styles hide the dark hover outline on focus/hover
        boxShadow: "none",
        ".MuiOutlinedInput-notchedOutline": { border: 0 },
      }}
    />
  );
};
